'use client';

import { c as _c } from "react/compiler-runtime";
import { requirementLabel } from '@shared/all/domains/factiiiRequirementOptions';
import type { AddFactiiiAction, AddFactiiiState } from '@shared/utils/helpers/addFactiii/formState';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import Image from 'next/image';
import type { Dispatch } from 'react';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Avatar from '../../common/Avatar';
import FactiiiSearchDropdown from '../../UI/FactiiiSearchDropdown';
import Switch from '../../UI/Switch';
import { t, ToggleRow } from './parts';
type Props = {
  state: AddFactiiiState;
  dispatch: Dispatch<AddFactiiiAction>;
};
const GroupPicker = t0 => {
  const $ = _c(29);
  const {
    state,
    dispatch
  } = t0;
  const {
    currentUser
  } = useUser();
  const [search, setSearch] = useState("");
  const t1 = currentUser?.username;
  let t2;
  if ($[0] !== search || $[1] !== state.spaceSlug || $[2] !== t1) {
    t2 = ["factiiis", "owned", state.spaceSlug, t1, search];
    $[0] = search;
    $[1] = state.spaceSlug;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentUser?.username || $[5] !== search || $[6] !== state.spaceSlug) {
    t3 = async () => {
      const [, data] = await client.search.factiiis.query({
        query: search,
        username: currentUser?.username,
        spaceSlug: state.spaceSlug || undefined
      });
      return data;
    };
    $[4] = currentUser?.username;
    $[5] = search;
    $[6] = state.spaceSlug;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = state.createAsGroup && Boolean(currentUser);
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: ownedResult
  } = useQuery(t5);
  let t6;
  if ($[12] !== ownedResult?.factiiis) {
    t6 = ownedResult?.factiiis ?? [];
    $[12] = ownedResult?.factiiis;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const owned = t6;
  let t7;
  if ($[14] !== dispatch) {
    t7 = createAsGroup => dispatch({
      type: "patch",
      patch: {
        createAsGroup
      }
    });
    $[14] = dispatch;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== state.createAsGroup || $[17] !== t7) {
    t8 = <ToggleRow title="Group this factiii" subtitle="Only one factiii in a group can be tagged per post"><Switch state={state.createAsGroup} onChange={t7} /></ToggleRow>;
    $[16] = state.createAsGroup;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== dispatch || $[20] !== owned || $[21] !== search || $[22] !== state.createAsGroup || $[23] !== state.groupFactiiiIds || $[24] !== state.name) {
    t9 = state.createAsGroup && <div className="space-y-2.5"><FactiiiSearchDropdown searchValue={search} onSearchChange={setSearch} searchResults={owned.filter(factiii => !state.groupFactiiiIds.includes(factiii.id))} onSelect={factiii_0 => dispatch({
        type: "patch",
        patch: {
          groupFactiiiIds: [...state.groupFactiiiIds, factiii_0.id]
        }
      })} placeholder="Search factiiis to group with..." label="Search factiiis to group with..." />{state.groupFactiiiIds.length === 0 ? <p className={t.meta}>Search above to add factiiis to this group.</p> : <div className="space-y-1.5"><div className="flex items-center gap-2.5 rounded-lg border border-primary bg-primary/5 px-2.5 py-1.5"><Image src="/images/display-image-default.png" alt={state.name} width={24} height={24} className="size-6 rounded-full object-cover" /><div className="min-w-0 flex-1"><p className={`truncate ${t.label}`}>{state.name || "New factiii"}</p><p className={t.meta}>Being created</p></div><span className={`shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 ${t.micro} text-primary`}>New</span></div>{state.groupFactiiiIds.map(id => {
          const factiii_1 = owned.find(candidate => candidate.id === id);
          if (!factiii_1) {
            return null;
          }
          return <div key={id} className="flex items-center gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-1.5"><Avatar factiiiAvatarKey={factiii_1.avatarKey} factiiiId={factiii_1.id} size={24} /><div className="min-w-0 flex-1"><p className={`truncate ${t.label}`}>{factiii_1.name}</p>{factiii_1.requirements?.length > 0 && <div className="mt-0.5 flex flex-wrap gap-1">{factiii_1.requirements.slice(0, 3).map(_temp)}{factiii_1.requirements.length > 3 && <span className="text-[10px] text-textSecondary">+{factiii_1.requirements.length - 3}</span>}</div>}</div><button type="button" onClick={() => dispatch({
              type: "patch",
              patch: {
                groupFactiiiIds: state.groupFactiiiIds.filter(groupId => groupId !== id)
              }
            })} className="shrink-0 rounded-md p-1 text-textSecondary transition-colors hover:bg-ds-border-standard hover:text-destructive" title="Remove"><X size={14} /></button></div>;
        })}</div>}</div>;
    $[19] = dispatch;
    $[20] = owned;
    $[21] = search;
    $[22] = state.createAsGroup;
    $[23] = state.groupFactiiiIds;
    $[24] = state.name;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = <div className="space-y-2.5">{t8}{t9}</div>;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
};
export default GroupPicker;
function _temp(req) {
  return <span key={req} className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-emphasis text-primary">{requirementLabel(req)}</span>;
}